import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from './Toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ItineraryManagement.css';

const ItineraryManagement = () => {
    const [itineraries, setItineraries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({}); // { dayIdx: percent }

    // Rich Text Editor Configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header', 'size', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'bullet', 'align'
    ];

    const emptyDay = {
        day: 1, title: '', description: '<p><br></p>', category: 'Sightseeing',
        meals: { breakfast: false, lunch: false, dinner: false },
        transport: '', from: '', to: '', cities: '', activities: '<p><br></p>'
    };

    const [formData, setFormData] = useState({
        name: '', destination: '', duration: { days: 1, nights: 0 },
        itinerary: [{ ...emptyDay }],
        inclusions: '<p><br></p>', // Default Quill empty content
        exclusions: '<p><br></p>', // Default Quill empty content
        importantNotes: '<p><br></p>', // Default Quill empty content
        termsAndConditions: '<p><br></p>' // Changed to string for rich text editor
    });

    const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => { fetchItineraries(); }, []);

    const fetchItineraries = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/saved-itinerary', { headers });
            setItineraries(res.data.itineraries || []);
        } catch (err) {
            toast.error('Failed to load itineraries');
        } finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.destination) {
            toast.error('Name and destination are required');
            return;
        }
        setSaving(true);
        try {
            // For rich text editor, we store HTML directly
            const cleanData = {
                ...formData,
                inclusions: formData.inclusions,
                exclusions: formData.exclusions,
                importantNotes: formData.importantNotes,
                termsAndConditions: formData.termsAndConditions
            };
            if (editingId) {
                await axios.put(`/saved-itinerary/${editingId}`, cleanData, { headers });
                toast.success('Itinerary updated!');
            } else {
                await axios.post('/saved-itinerary', cleanData, { headers });
                toast.success('Itinerary saved!');
            }
            resetForm();
            fetchItineraries();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };

    const handleEdit = (item) => {
        setFormData({
            name: item.name, destination: item.destination,
            duration: item.duration || { days: 1, nights: 0 },
            itinerary: item.itinerary?.length ? item.itinerary : [{ ...emptyDay }],
            inclusions: item.inclusions || '<p><br></p>',
            exclusions: item.exclusions || '<p><br></p>',
            importantNotes: item.importantNotes || '<p><br></p>',
            termsAndConditions: item.termsAndConditions || '<p><br></p>'
        });
        setEditingId(item._id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this itinerary?')) return;
        try {
            await axios.delete(`/saved-itinerary/${id}`, { headers });
            toast.success('Deleted!');
            fetchItineraries();
        } catch (err) { toast.error('Failed to delete'); }
    };

    const resetForm = () => {
        setFormData({
            name: '', destination: '', duration: { days: 1, nights: 0 },
            itinerary: [{ ...emptyDay }],
            inclusions: '<p><br></p>', 
            exclusions: '<p><br></p>', 
            importantNotes: '<p><br></p>', 
            termsAndConditions: '<p><br></p>'
        });
        setEditingId(null);
        setShowForm(false);
    };

    const addDay = () => {
        setFormData(prev => ({
            ...prev,
            itinerary: [...prev.itinerary, { ...emptyDay, day: prev.itinerary.length + 1 }]
        }));
    };

    const removeDay = (idx) => {
        setFormData(prev => ({
            ...prev,
            itinerary: prev.itinerary.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }))
        }));
    };

    const updateDay = (idx, field, value) => {
        setFormData(prev => ({
            ...prev,
            itinerary: prev.itinerary.map((d, i) => i === idx ? { ...d, [field]: value } : d)
        }));
    };

    // Helper function to parse **bold** syntax (not needed anymore, but keeping for backward compatibility)
    const parseBoldText = (text) => {
        if (!text) return '';
        return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    };

    // Helper function to extract text from HTML for list view
    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Helper function to count items in HTML (by counting <li> or <p> tags)
    const countHtmlItems = (html) => {
        if (!html) return 0;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const items = tmp.querySelectorAll('li, p');
        return items.length || (html.trim() ? 1 : 0);
    };

    const handleShareWhatsApp = (item) => {
        const message = `🌍 *${item.name}*\n\n📍 Destination: ${item.destination}\n📅 Duration: ${item.duration?.days || 0} Days / ${item.duration?.nights || 0} Nights\n\n✨ Check out this amazing itinerary from Gajanan World Tourism!\n\nFor more details, contact us.`;
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    };

    const handleShareEmail = async (item) => {
        // Prompt for customer email
        const customerEmail = prompt('Enter customer email address:');
        if (!customerEmail) return;

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(customerEmail)) {
            toast.error('Please enter a valid email address');
            return;
        }

        const customerName = prompt('Enter customer name (optional):') || 'Valued Customer';

        try {
            toast.info('Generating PDF and sending email...');

            // Generate HTML content for PDF (same as PDF download)
            const htmlContent = generatePDFHTML(item);

            // Send email with PDF
            const response = await axios.post('/email/send-itinerary', {
                customerEmail,
                customerName,
                itineraryData: {
                    name: item.name,
                    destination: item.destination,
                    duration: item.duration
                },
                htmlContent
            }, { headers });

            if (response.data.success) {
                toast.success(`Itinerary sent successfully to ${customerEmail}`);
            } else {
                toast.error(response.data.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            console.error('Error response:', error.response?.data);
            if (error.response?.data?.code === 'EMAIL_NOT_CONFIGURED') {
                toast.error('Email not configured. Please set up Gmail in settings.');
            } else {
                const errorMsg = error.response?.data?.message || error.message || 'Failed to send email';
                toast.error(errorMsg);
            }
        }
    };

    // Function to generate PDF HTML (extracted from handleDownloadPDF)
    const generatePDFHTML = (item) => {
        const categoryColors = {
            'Sightseeing': '#1565c0', 'Adventure': '#e65100', 'Beach': '#00838f',
            'Shopping': '#6a1b9a', 'Cultural': '#ad1457', 'Religious': '#4e342e',
            'Nature': '#2e7d32', 'Food & Dining': '#bf360c', 'Leisure': '#00695c',
            'Transfer': '#37474f', 'Free Day': '#558b2f', 'Other': '#455a64'
        };

        const daysHtml = (item.itinerary || []).map(day => {
            const catColor = categoryColors[day.category] || '#1565c0';
            const imgs = (day.images || []).map(img =>
                `<div style="flex:0 0 auto"><img src="${img.url || img}" style="width:210px;height:140px;object-fit:cover;border-radius:10px;box-shadow:0 4px 15px rgba(0,0,0,0.15)" /></div>`
            ).join('');

            const mealsArr = [];
            if (day.meals?.breakfast) mealsArr.push('<span style="background:#e8f5e9;color:#2e7d32;padding:5px 12px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:0.3px">🍳 Breakfast</span>');
            if (day.meals?.lunch) mealsArr.push('<span style="background:#fff3e0;color:#e65100;padding:5px 12px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:0.3px">🍱 Lunch</span>');
            if (day.meals?.dinner) mealsArr.push('<span style="background:#fce4ec;color:#c62828;padding:5px 12px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:0.3px">🍽️ Dinner</span>');

            const infoPills = [];
            if (day.transport) infoPills.push(`<span style="background:#e8eaf6;color:#283593;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:500">🚗 ${day.transport}</span>`);
            if (day.from && day.to) infoPills.push(`<span style="background:#e0f2f1;color:#00695c;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:500">📍 ${day.from} → ${day.to}</span>`);
            else if (day.from) infoPills.push(`<span style="background:#e0f2f1;color:#00695c;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:500">📍 From: ${day.from}</span>`);
            else if (day.to) infoPills.push(`<span style="background:#e0f2f1;color:#00695c;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:500">📍 To: ${day.to}</span>`);
            if (day.cities) infoPills.push(`<span style="background:#f3e5f5;color:#6a1b9a;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:500">🏙️ ${day.cities}</span>`);

            return `
                <div style="margin-bottom:28px;page-break-inside:avoid;display:flex;gap:0">
                    <div style="width:4px;background:linear-gradient(180deg,${catColor},#c9a94e);border-radius:4px;flex-shrink:0"></div>
                    <div style="flex:1;margin-left:20px">
                        <div style="display:flex;align-items:center;gap:16px;margin-bottom:14px">
                            <div style="background:linear-gradient(135deg,#1a237e,#283593);color:white;width:64px;height:64px;border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(26,35,126,0.3);flex-shrink:0">
                                <div style="font-size:22px;font-weight:800;line-height:1">${day.day}</div>
                                <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;opacity:0.8">Day</div>
                            </div>
                            <div>
                                <h3 style="margin:0 0 4px 0;font-size:18px;color:#1a237e;font-weight:700;font-family:'Playfair Display',Georgia,serif">${day.title || 'Day ' + day.day}</h3>
                                ${day.category ? `<span style="background:${catColor}15;color:${catColor};padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;border:1px solid ${catColor}30">${day.category}</span>` : ''}
                            </div>
                        </div>
                        ${imgs ? `<div style="display:flex;gap:12px;margin-bottom:14px;overflow-x:auto">${imgs}</div>` : ''}
                        ${day.description ? `<div style="font-size:13px;line-height:1.8;color:#424242;margin:0 0 12px 0;text-align:justify">${day.description}</div>` : ''}
                        ${day.activities ? `
                            <div style="padding:12px 16px;background:linear-gradient(135deg,#fffde7,#fff9c4);border-left:4px solid #f9a825;border-radius:0 8px 8px 0;margin-bottom:10px">
                                <p style="font-weight:700;font-size:11px;color:#f57f17;margin:0 0 4px 0;text-transform:uppercase;letter-spacing:0.5px">🎯 Activities & Sightseeing</p>
                                <div style="font-size:12px;color:#5d4037;margin:0;line-height:1.6">${day.activities}</div>
                            </div>` : ''}
                        ${infoPills.length ? `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px">${infoPills.join('')}</div>` : ''}
                        ${mealsArr.length ? `
                            <div style="display:flex;gap:8px;flex-wrap:wrap;padding-top:10px;border-top:1px dashed #e0e0e0">
                                <span style="font-size:10px;color:#757575;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:5px 0">Meals:</span>
                                ${mealsArr.join('')}
                            </div>` : ''}
                    </div>
                </div>`;
        }).join('');

        const inclusionsHtml = item.inclusions ? `
            <div style="padding:12px 16px;background:#fafffe;border:1px solid #c8e6c9;border-radius:8px">
                ${item.inclusions}
            </div>
        ` : '';

        const exclusionsHtml = item.exclusions ? `
            <div style="padding:12px 16px;background:#fffafa;border:1px solid #ffcdd2;border-radius:8px">
                ${item.exclusions}
            </div>
        ` : '';

        const importantNotesHtml = item.importantNotes ? `
            <div style="padding:12px 16px;background:#f0f7ff;border:1px solid #bbdefb;border-radius:8px">
                ${item.importantNotes}
            </div>
        ` : '';

        const termsHtml = item.termsAndConditions ? `
            <div style="padding:12px 16px;background:#fafaf5;border:1px solid #e8e0c8;border-radius:8px">
                ${item.termsAndConditions}
            </div>
        ` : '';

        return `
            <!DOCTYPE html>
            <html><head><title>${item.name} - Itinerary</title>
            <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
            <style>
                @page { size: A4 portrait; margin: 12mm; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
                body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; background: white; color: #333; word-wrap: break-word; overflow-wrap: break-word; }
                h1, h2, h3 { font-family: 'Playfair Display', Georgia, serif; word-wrap: break-word; }
                img { max-width: 100%; height: auto; }
                @media print { body { padding: 0; } }
            </style>
            </head><body>
                <div style="background:linear-gradient(135deg,#0d1b3e 0%,#1a237e 50%,#283593 100%);padding:35px 30px;border-radius:12px;color:white;position:relative;overflow:hidden;margin-bottom:30px">
                    <div style="position:relative;z-index:1">
                        <div style="margin-bottom:18px;padding-bottom:12px;border-bottom:1px solid rgba(201,169,78,0.3)">
                            <h1 style="margin:0;font-size:24px;font-weight:700;letter-spacing:1px">Gajanan World Tourism</h1>
                            <p style="margin:5px 0 0 0;font-size:11px;color:#c9a94e;text-transform:uppercase;letter-spacing:3px;font-family:'Inter',sans-serif;font-weight:500">Your Premium Holiday Consultant</p>
                        </div>
                        <h2 style="margin:0 0 8px 0;font-size:24px;font-weight:800;color:#c9a94e;word-wrap:break-word">${item.name}</h2>
                        <div style="display:flex;gap:15px;flex-wrap:wrap;margin-top:10px">
                            <span style="background:rgba(255,255,255,0.1);padding:6px 14px;border-radius:25px;font-size:12px;font-family:'Inter',sans-serif;border:1px solid rgba(201,169,78,0.3)">📍 ${item.destination}</span>
                            <span style="background:rgba(201,169,78,0.2);padding:6px 14px;border-radius:25px;font-size:12px;font-family:'Inter',sans-serif;font-weight:600;color:#c9a94e;border:1px solid rgba(201,169,78,0.3)">📅 ${item.duration?.days || 0} Days / ${item.duration?.nights || 0} Nights</span>
                        </div>
                    </div>
                </div>
                <div style="padding:0">
                    <div style="display:flex;align-items:center;gap:12px;margin-bottom:25px">
                        <div style="width:5px;height:35px;background:linear-gradient(180deg,#c9a94e,#1a237e);border-radius:3px"></div>
                        <h2 style="margin:0;font-size:22px;color:#1a237e;font-weight:700">Detailed Itinerary</h2>
                    </div>
                    ${daysHtml}
                </div>
                ${inclusionsHtml || exclusionsHtml || importantNotesHtml ? `
                <div style="page-break-inside:avoid;margin-top:35px;padding:0 5px">
                    <div style="display:flex;align-items:center;gap:12px;margin-bottom:20px">
                        <div style="width:5px;height:35px;background:linear-gradient(180deg,#4CAF50,#f44336);border-radius:3px"></div>
                        <h2 style="margin:0;font-size:22px;color:#1a237e;font-weight:700">Package Details</h2>
                    </div>
                    ${inclusionsHtml ? `<div style="margin-bottom:20px"><h3 style="margin:0 0 12px 0;font-size:15px;color:#2e7d32;display:flex;align-items:center;gap:8px;font-family:'Inter',sans-serif;font-weight:700"><span style="background:#4CAF50;color:white;width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:14px">✓</span>Inclusions</h3>${inclusionsHtml}</div>` : ''}
                    ${exclusionsHtml ? `<div style="margin-bottom:20px"><h3 style="margin:0 0 12px 0;font-size:15px;color:#c62828;display:flex;align-items:center;gap:8px;font-family:'Inter',sans-serif;font-weight:700"><span style="background:#f44336;color:white;width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:14px">✗</span>Exclusions</h3>${exclusionsHtml}</div>` : ''}
                    ${importantNotesHtml ? `<div style="margin-bottom:20px"><h3 style="margin:0 0 12px 0;font-size:15px;color:#1565c0;display:flex;align-items:center;gap:8px;font-family:'Inter',sans-serif;font-weight:700"><span style="background:#2196F3;color:white;width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:14px">📌</span>Important Notes</h3>${importantNotesHtml}</div>` : ''}
                </div>` : ''}
                ${termsHtml ? `<div style="page-break-inside:avoid;margin-top:30px;padding:0 5px"><div style="display:flex;align-items:center;gap:12px;margin-bottom:15px"><div style="width:5px;height:35px;background:linear-gradient(180deg,#c9a94e,#8d6e3a);border-radius:3px"></div><h2 style="margin:0;font-size:22px;color:#1a237e;font-weight:700">Terms & Conditions</h2></div>${termsHtml}</div>` : ''}
                <div style="margin-top:45px;padding:25px 30px;background:linear-gradient(135deg,#0d1b3e,#1a237e);border-radius:12px;color:white;text-align:center">
                    <p style="margin:0 0 5px 0;font-size:16px;font-family:'Playfair Display',serif;font-weight:600;color:#c9a94e">Thank you for choosing</p>
                    <p style="margin:0 0 10px 0;font-size:22px;font-family:'Playfair Display',serif;font-weight:800;letter-spacing:1px">Gajanan World Tourism</p>
                    <div style="width:60px;height:2px;background:#c9a94e;margin:12px auto"></div>
                    <p style="margin:10px 0 0 0;font-size:11px;color:rgba(255,255,255,0.6);font-family:'Inter',sans-serif">We look forward to making your travel experience memorable</p>
                </div>
            </body></html>
        `;
    };

    const handleDownloadPDF = (item) => {
        const htmlContent = generatePDFHTML(item);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        setTimeout(() => { printWindow.print(); }, 800);
    };




    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">📋 Manage Itineraries</h2>
                <button
                    onClick={() => { resetForm(); setShowForm(true); }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
                >
                    + Add New Itinerary
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="bg-white border-2 border-purple-200 rounded-xl p-6 mb-6 shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-purple-800">
                            {editingId ? '✏️ Edit Itinerary' : '➕ New Itinerary'}
                        </h3>
                        <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Basic Info */}
                        <div className="grid grid-cols-4 gap-4 mb-6">
                            <div className="col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Itinerary Name *</label>
                                <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                                    placeholder="e.g., Dubai Classic 4D/3N" className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Destination *</label>
                                <input type="text" value={formData.destination} onChange={e => setFormData(p => ({ ...p, destination: e.target.value }))}
                                    placeholder="e.g., Dubai" className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">🌙 Nights</label>
                                <input type="number" min="1" value={formData.duration.nights}
                                    onChange={e => {
                                        const nights = parseInt(e.target.value) || 1;
                                        const days = nights + 1;
                                        // Auto-generate itinerary days
                                        const currentDays = formData.itinerary;
                                        let newItinerary;
                                        if (days > currentDays.length) {
                                            newItinerary = [...currentDays];
                                            for (let i = currentDays.length; i < days; i++) {
                                                newItinerary.push({ ...emptyDay, day: i + 1 });
                                            }
                                        } else {
                                            newItinerary = currentDays.slice(0, days).map((d, i) => ({ ...d, day: i + 1 }));
                                        }
                                        setFormData(p => ({ ...p, duration: { days, nights }, itinerary: newItinerary }));
                                    }}
                                    className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-lg font-bold text-center" />
                                <p className="text-xs text-purple-600 mt-1 font-semibold text-center">
                                    📅 {formData.duration.nights + 1} Days / {formData.duration.nights} Nights
                                </p>
                            </div>
                        </div>

                        {/* Day-wise Itinerary */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-lg font-bold text-gray-800">📅 Day-wise Itinerary</h4>
                                <button type="button" onClick={addDay} className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">+ Add Day</button>
                            </div>

                            {formData.itinerary.map((day, idx) => (
                                <div key={idx} className="border-2 border-gray-100 rounded-lg p-4 mb-3 bg-gray-50">
                                    <div className="flex justify-between items-center mb-3">
                                        <h5 className="font-bold text-purple-700">🗺️ Day {day.day}</h5>
                                        {formData.itinerary.length > 1 && (
                                            <button type="button" onClick={() => removeDay(idx)} className="text-red-500 hover:text-red-700 text-sm">🗑️ Remove</button>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <input type="text" placeholder="Day Title (e.g., Arrival in Dubai)" value={day.title}
                                            onChange={e => updateDay(idx, 'title', e.target.value)}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500" />
                                        
                                        {/* Description - Rich Text Editor */}
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">📝 Description</label>
                                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={day.description || '<p><br></p>'}
                                                    onChange={(value) => updateDay(idx, 'description', value)}
                                                    modules={quillModules}
                                                    formats={quillFormats}
                                                    placeholder="Enter day description... Select text to format"
                                                    style={{ minHeight: '150px', background: 'white' }}
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">📌 Category</label>
                                                <select value={day.category} onChange={e => updateDay(idx, 'category', e.target.value)}
                                                    className="w-full px-3 py-2 border rounded-lg text-sm">
                                                    {['Sightseeing', 'Adventure', 'Beach', 'Shopping', 'Cultural', 'Religious', 'Nature', 'Food & Dining', 'Leisure', 'Transfer', 'Free Day', 'Other']
                                                        .map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">🍽️ Meals</label>
                                                <div className="flex items-center gap-4 mt-1">
                                                    {[['breakfast', '🍳 B'], ['lunch', '🍱 L'], ['dinner', '🍽️ D']].map(([key, label]) => (
                                                        <label key={key} className="flex items-center gap-1 text-sm cursor-pointer">
                                                            <input type="checkbox" checked={day.meals?.[key] || false}
                                                                onChange={e => updateDay(idx, 'meals', { ...day.meals, [key]: e.target.checked })} />
                                                            <span>{label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-3">
                                            <input type="text" placeholder="🚗 Transport" value={day.transport || ''}
                                                onChange={e => updateDay(idx, 'transport', e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm" />
                                            <input type="text" placeholder="📍 From" value={day.from || ''}
                                                onChange={e => updateDay(idx, 'from', e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm" />
                                            <input type="text" placeholder="📍 To" value={day.to || ''}
                                                onChange={e => updateDay(idx, 'to', e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm" />
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" placeholder="🏙️ Cities (comma separated)" value={day.cities || ''}
                                                onChange={e => updateDay(idx, 'cities', e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm" />
                                        </div>

                                        {/* Activities - Rich Text Editor */}
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">🎯 Activities & Sightseeing</label>
                                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={day.activities || '<p><br></p>'}
                                                    onChange={(value) => updateDay(idx, 'activities', value)}
                                                    modules={quillModules}
                                                    formats={quillFormats}
                                                    placeholder="Enter activities and sightseeing... Select text to format"
                                                    style={{ minHeight: '120px', background: 'white' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Day Images */}
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">🖼️ Day Images (max 3)</label>
                                            <div className="flex flex-wrap gap-3 mb-2">
                                                {(day.images || []).map((img, imgIdx) => (
                                                    <div key={imgIdx} className="relative group">
                                                        <img src={img.url || img} alt={`Day ${day.day} - ${imgIdx + 1}`}
                                                            className="w-24 h-20 object-cover rounded-lg border-2 border-purple-200 shadow-sm" />
                                                        <button type="button"
                                                            onClick={() => {
                                                                const newImages = (day.images || []).filter((_, i) => i !== imgIdx);
                                                                updateDay(idx, 'images', newImages);
                                                            }}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                                {(day.images || []).length < 3 && !uploadProgress[idx] && (
                                                    <label className="w-24 h-20 border-2 border-dashed border-purple-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                                                        <span className="text-purple-400 text-lg">📷</span>
                                                        <span className="text-xs text-purple-400">Add</span>
                                                        <input type="file" accept="image/*" className="hidden"
                                                            onChange={async (e) => {
                                                                const file = e.target.files[0];
                                                                if (!file) return;
                                                                try {
                                                                    setUploadProgress(p => ({ ...p, [idx]: 0 }));
                                                                    const fd = new FormData();
                                                                    fd.append('image', file);
                                                                    fd.append('folder', 'itinerary');
                                                                    const res = await axios.post('/upload/image', fd, {
                                                                        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                                                                        onUploadProgress: (progressEvent) => {
                                                                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                                                            setUploadProgress(p => ({ ...p, [idx]: percent }));
                                                                        }
                                                                    });
                                                                    const newImages = [...(day.images || []), { url: res.data.url, caption: '' }];
                                                                    updateDay(idx, 'images', newImages);
                                                                    toast.success('Image uploaded!');
                                                                } catch (err) {
                                                                    toast.error('Failed to upload image');
                                                                }
                                                                setUploadProgress(p => { const n = { ...p }; delete n[idx]; return n; });
                                                                e.target.value = '';
                                                            }} />
                                                    </label>
                                                )}
                                                {uploadProgress[idx] !== undefined && (
                                                    <div className="w-24 h-20 border-2 border-purple-400 rounded-lg flex flex-col items-center justify-center bg-purple-50">
                                                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                                                            <div className="h-full bg-purple-600 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[idx]}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-bold text-purple-700">{uploadProgress[idx]}%</span>
                                                        <span className="text-[10px] text-purple-500">Uploading...</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Inclusions - Rich Text Editor */}
                        <div className="mb-6">
                            <h4 className="text-lg font-bold text-green-700 mb-2">✅ Inclusions</h4>
                            <p className="text-xs text-gray-500 mb-2">💡 Select text to format: Bold, Color, Size, etc. (like MS Word)</p>
                            <div className="border-2 border-green-200 rounded-lg overflow-hidden">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.inclusions || '<p><br></p>'}
                                    onChange={(value) => setFormData(p => ({ ...p, inclusions: value }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter inclusions here... Select text to format"
                                    style={{ minHeight: '200px', background: 'white' }}
                                />
                            </div>
                        </div>

                        {/* Exclusions - Rich Text Editor */}
                        <div className="mb-6">
                            <h4 className="text-lg font-bold text-red-700 mb-2">❌ Exclusions</h4>
                            <p className="text-xs text-gray-500 mb-2">💡 Select text to format: Bold, Color, Size, etc. (like MS Word)</p>
                            <div className="border-2 border-red-200 rounded-lg overflow-hidden">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.exclusions || '<p><br></p>'}
                                    onChange={(value) => setFormData(p => ({ ...p, exclusions: value }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter exclusions here... Select text to format"
                                    style={{ minHeight: '200px', background: 'white' }}
                                />
                            </div>
                        </div>

                        {/* Important Notes - Rich Text Editor */}
                        <div className="mb-6">
                            <h4 className="text-lg font-bold text-blue-700 mb-2">📌 Important Notes</h4>
                            <p className="text-xs text-gray-500 mb-2">💡 Select text to format: Bold, Color, Size, etc. (like MS Word)</p>
                            <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.importantNotes || '<p><br></p>'}
                                    onChange={(value) => setFormData(p => ({ ...p, importantNotes: value }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter important notes here... Select text to format"
                                    style={{ minHeight: '200px', background: 'white' }}
                                />
                            </div>
                        </div>

                        {/* Terms & Conditions - Rich Text Editor */}
                        <div className="mb-6">
                            <h4 className="text-lg font-bold text-amber-700 mb-2">📋 Terms & Conditions</h4>
                            <p className="text-xs text-gray-500 mb-2">💡 Select text to format: Bold, Color, Size, etc. (like MS Word)</p>
                            <div className="border-2 border-amber-200 rounded-lg overflow-hidden">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.termsAndConditions || '<p><br></p>'}
                                    onChange={(value) => setFormData(p => ({ ...p, termsAndConditions: value }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter terms and conditions here... Select text to format"
                                    style={{ minHeight: '200px', background: 'white' }}
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3">
                            <button type="submit" disabled={saving}
                                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50">
                                {saving ? '⏳ Saving...' : (editingId ? '💾 Update Itinerary' : '💾 Save Itinerary')}
                            </button>
                            <button type="button" onClick={resetForm}
                                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading itineraries...</p>
                </div>
            ) : itineraries.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <p className="text-6xl mb-4">📋</p>
                    <p className="text-xl text-gray-600 font-semibold">No itineraries yet</p>
                    <p className="text-gray-500 mt-2">Create your first itinerary to get started</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {itineraries.map(item => (
                        <div key={item._id} className="bg-white border-2 border-gray-100 rounded-xl p-5 hover:shadow-lg transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">{item.name}</h3>
                                    <p className="text-sm text-purple-600">📍 {item.destination}</p>
                                </div>
                                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                                    {item.duration?.days || 0}D/{item.duration?.nights || 0}N
                                </span>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-3 text-xs">
                                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">📅 {item.itinerary?.length || 0} Days</span>
                                {item.inclusions && (
                                    <span className="bg-green-50 text-green-700 px-2 py-1 rounded">✅ Inclusions</span>
                                )}
                                {item.exclusions && (
                                    <span className="bg-red-50 text-red-700 px-2 py-1 rounded">❌ Exclusions</span>
                                )}
                                {item.importantNotes && (
                                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">📌 Notes</span>
                                )}
                                {item.termsAndConditions && (
                                    <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded">📋 T&C</span>
                                )}
                            </div>

                            {item.itinerary?.slice(0, 3).map((day, i) => (
                                <div key={i} className="text-xs text-gray-600 mb-1">
                                    <span className="font-semibold text-purple-600">Day {day.day}:</span> {day.title}
                                    {day.meals && (
                                        <span className="ml-2">
                                            {day.meals.breakfast && '🍳'}
                                            {day.meals.lunch && '🍱'}
                                            {day.meals.dinner && '🍽️'}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {(item.itinerary?.length || 0) > 3 && (
                                <p className="text-xs text-gray-400">+{item.itinerary.length - 3} more days...</p>
                            )}

                            <div className="flex gap-2 mt-4 pt-3 border-t">
                                <button onClick={() => handleEdit(item)}
                                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium">✏️ Edit</button>
                                <button onClick={() => handleDownloadPDF(item)}
                                    className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium">📥 PDF</button>
                                <button onClick={() => handleShareEmail(item)}
                                    className="flex-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm font-medium">📧 Email</button>
                                <button onClick={() => handleShareWhatsApp(item)}
                                    className="flex-1 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 text-sm font-medium">📱 WhatsApp</button>
                                <button onClick={() => handleDelete(item._id)}
                                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium">🗑️</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ItineraryManagement;
